import React, { useId } from 'react';
import classNames from 'classnames';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Badge from 'components/partials/badge/badge';
import Tooltip from 'components/partials/tooltip/tooltip';

import Icons from 'assets/icons';

import { getTestId } from 'helpers/test-helpers';

import type { MetricCardData, MetricLineData } from '../results-step.types';

interface MetricCardProps {
  card: MetricCardData;
  actualYearLabel?: string;
  isLoading?: boolean;
}

interface MetricLineProps {
  line: MetricLineData;
  cardKey: string;
  actualYearLabel: string;
}

const MetricLine = ({
  line,
  cardKey,
  actualYearLabel,
}: MetricLineProps): React.JSX.Element => {
  const tooltipDescriptionId = useId();
  const { prefix, amount, suffix } = line.value;

  const content = (
    <Stack
      data-testid={`results-metric-line-${line.key}`}
      sx={{ alignItems: 'flex-start', gap: 0.5 }}
    >
      <span className="text-sm font-semibold text-gray-500">{line.label}</span>
      <span className="whitespace-nowrap">
        {prefix && (
          <span className="text-lg font-normal text-gray-500">{prefix}</span>
        )}
        <span className="text-lg font-bold text-gray-700">{amount}</span>
        {suffix && (
          <span className="text-lg font-normal text-gray-500">{suffix}</span>
        )}
      </span>
      {line.originalValue && (
        <>
          <Tooltip
            content={line.originalTooltip}
            position="right"
            positionStrategy="fixed"
            customClass="!bg-gray-700"
            anchorStrategy="child"
          >
            <Badge
              variant="neutral-outlined"
              text={line.originalValue}
              tabIndex={0}
              aria-describedby={tooltipDescriptionId}
            />
          </Tooltip>
          <span id={tooltipDescriptionId} className="sr-only">
            {line.originalTooltip}
          </span>
        </>
      )}
      {line.actualValue && (
        <span
          className="text-xs text-gray-500"
          data-testid={getTestId({
            prefix: 'results-metric-card',
            label: cardKey,
            suffix: 'actual',
          })}
        >
          {actualYearLabel}:{' '}
          <span className="font-medium">{line.actualValue}</span>
        </span>
      )}
    </Stack>
  );

  if (line.isCollapsed === undefined) {
    return content;
  }

  return (
    <div
      className={classNames(
        'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
        line.isCollapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
      )}
      aria-hidden={line.isCollapsed}
      inert={line.isCollapsed}
      data-testid={`results-metric-line-wrapper-${line.key}`}
    >
      <Box sx={{ minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ pt: 1.5 }}>{content}</Box>
      </Box>
    </div>
  );
};

const MetricCard = ({
  card,
  actualYearLabel = 'Actual',
  isLoading = false,
}: MetricCardProps): React.JSX.Element => (
  <Stack
    sx={{
      position: 'relative',
      flex: '1 1 0%',
      gap: 0.5,
      borderRadius: '0.375rem',
      borderWidth: '1px',
      borderColor: 'tokens.navy.200',
      bgcolor: 'common.white',
      px: 2,
      py: 1.5,
      overflow: 'hidden',
    }}
    data-testid={getTestId({
      prefix: 'results-metric-card',
      label: card.key,
    })}
  >
    <div
      className={classNames(
        'flex flex-col transition-[filter] duration-200',
        isLoading && 'blur-[2px]'
      )}
    >
      {card.lines.map((line) => (
        <MetricLine
          key={line.key}
          line={line}
          cardKey={card.key}
          actualYearLabel={actualYearLabel}
        />
      ))}
    </div>
    {isLoading && (
      <Stack
        direction="row"
        sx={{
          position: 'absolute',
          inset: 0,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icons.Spinner className="size-5 animate-spin text-gray-300 fill-navy-600" />
      </Stack>
    )}
  </Stack>
);

export default MetricCard;
